import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import useAppStore from '../store/useAppStore';
import type { AdChannel } from '../lib/adPermissions';
import { getPermissionApplyUrl } from '../lib/gatekeeperUrls';
import { hasChannelAccess } from '../lib/adPermissions';

function getChannelLabel(channel: AdChannel): string {
  switch (channel) {
    case 'facebook':
      return 'Facebook';
    case 'google':
      return 'Google';
    case 'tiktok':
      return 'TikTok';
    default:
      return '该渠道';
  }
}

function ChannelPermissionBlocked({ channel }: { channel: AdChannel }) {
  const channelLabel = getChannelLabel(channel);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        minHeight: 400,
      }}
    >
      <div style={{ textAlign: 'center', maxWidth: 360 }}>
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: '#FEF2F2',
            margin: '0 auto 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#A32D2D" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0110 0v4" />
          </svg>
        </div>
        <div style={{ fontSize: 18, fontWeight: 600, color: '#333', marginBottom: 8 }}>无访问权限</div>
        <div style={{ fontSize: 14, color: '#888', marginBottom: 4 }}>
          你没有 {channelLabel} 渠道的访问权限
        </div>
        <div style={{ fontSize: 13, color: '#AAA', marginBottom: 20 }}>
          请去权限管理平台申请对应渠道权限
        </div>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <a
            href={getPermissionApplyUrl()}
            target="_blank"
            rel="noreferrer"
            style={{
              fontSize: 13,
              padding: '8px 20px',
              borderRadius: 6,
              background: '#1210B8',
              color: '#fff',
              border: 'none',
              textDecoration: 'none',
              cursor: 'pointer',
            }}
          >
            申请权限
          </a>
        </div>
      </div>
    </div>
  );
}

export function RequireChannelAccess({
  channel,
  children,
}: {
  channel?: AdChannel;
  children: ReactNode;
  fallbackBasePath?: string;
}) {
  const params = useParams<{ platform?: string }>();
  const permissionsCheckDone = useAppStore((s) => s.permissionsCheckDone);
  const permissionsLoaded = useAppStore((s) => s.permissionsLoaded);
  const sitesLoaded = useAppStore((s) => s.sitesLoaded);
  const adContext = useAppStore((s) => s.adContext);
  const routeChannel = params.platform;

  const ready = permissionsCheckDone && permissionsLoaded && sitesLoaded;
  if (!ready) return null;

  const effectiveChannel = (
    channel ??
    (routeChannel === 'facebook' || routeChannel === 'google' || routeChannel === 'tiktok'
      ? routeChannel
      : undefined)
  ) as AdChannel | undefined;

  if (!effectiveChannel) {
    return <>{children}</>;
  }

  if (hasChannelAccess(adContext?.channel_access, effectiveChannel)) {
    return <>{children}</>;
  }

  return <ChannelPermissionBlocked channel={effectiveChannel} />;
}
